import { useCallback, useEffect } from "react"
import { useSelector } from "react-redux"
import { Path, useLocation, useSearchParams } from "react-router-dom"
import {
  selectPractice,
  selectSelectedLocation,
} from "../../domain/Practice/model"
import { selectProvider } from "../../domain/Provider/model"
import {
  AFFILIATE_RELATIONS_ACTIONS_ROUTE,
  PAYMENTS_BASE_ROUTE,
  PRACTICE_PROFILE_BASE_ROUTE,
  PROVIDER_PROFILE_BASE_ROUTE,
  REFERRALS_BASE_ROUTE,
  appQueryParams,
  appRoutes,
} from "../../routes"
import { useNavigateWithQueryString } from "../../App"

/**
 * Custom hook to synchronize query parameters with Redux state.
 * It adds or removes query parameters based on the current Redux state and the current route.
 */
export const useQueryParamsSync = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigateWithQueryString()
  const practice = useSelector(selectPractice)
  const provider = useSelector(selectProvider)
  const selectedLocation = useSelector(selectSelectedLocation)

  /**
   * Adds new query parameter IDs from Redux state if they don't already exist in the search params.
   *
   * @param {URLSearchParams} searchParams - The current search parameters.
   * @param {string} practiceId - The practice ID from Redux state.
   * @param {string} providerId - The provider ID from Redux state.
   * @param {string} selectedLocationId - The selected location ID from Redux state.
   */
  const addNewQueryParamIdsFromRedux = useCallback(
    (
      searchParams: URLSearchParams,
      practiceId: string,
      providerId: string,
      selectedLocationId: string,
    ) => {
      if (!!practiceId && !searchParams.has(appQueryParams.PRACTICE_ID)) {
        searchParams.append(appQueryParams.PRACTICE_ID, practiceId)
      }
      if (!!providerId && !searchParams.has(appQueryParams.PROVIDER_ID)) {
        searchParams.append(appQueryParams.PROVIDER_ID, providerId)
      }
      if (
        !!selectedLocationId &&
        !searchParams.has(appQueryParams.LOCATION_ID)
      ) {
        searchParams.append(appQueryParams.LOCATION_ID, selectedLocationId)
      }
    },
    [],
  )

  /**
   * Removes query parameter IDs if they are not needed based on the current route.
   *
   * @param {string | Partial<Path>} path - The current route path.
   * @param {URLSearchParams} searchParams - The current search parameters.
   */
  const removeIdsIfNotNeeded = useCallback(
    (path: string | Partial<Path>, searchParams: URLSearchParams) => {
      const pathString = typeof path === "string" ? path : path.pathname || ""
      if (
        !pathString.includes(PRACTICE_PROFILE_BASE_ROUTE) &&
        !pathString.includes(appRoutes.taskboard.INDEX_ROUTE) &&
        !pathString.includes(appRoutes.payments) &&
        !pathString.includes(appRoutes.ADP_SSO) &&
        !pathString.includes(appRoutes.referrals.INDEX_ROUTE) &&
        /** For providers that are associated with more than one practice: when navigating to Liability Insurance, the Practice ID will be used to determine the correct practice context and should not be removed */
        !pathString.includes(appRoutes.provider.LIABILITY_INSURANCE_ROUTE)
      ) {
        searchParams.delete(appQueryParams.PRACTICE_ID)
      }
      if (pathString.includes(appRoutes.practiceInvitations.INDEX_ROUTE)) {
        searchParams.delete(appQueryParams.PRACTICE_ID)
      } else {
        searchParams.delete(appQueryParams.USER_ID)
      }
      if (!pathString.includes(appRoutes.practiceInvitations.ACCEPT_ROUTE)) {
        searchParams.delete(appQueryParams.PRACTICE_INVITATION_ID)
      }
      if (!pathString.includes(PROVIDER_PROFILE_BASE_ROUTE)) {
        searchParams.delete(appQueryParams.PROVIDER_ID)
      }
      if (!pathString.includes(appRoutes.practice.location.INDEX_ROUTE)) {
        searchParams.delete(appQueryParams.LOCATION_ID)
      }
      if (!pathString.includes(REFERRALS_BASE_ROUTE)) {
        searchParams.delete(appQueryParams.REFERRAL_FILTER_BY)
        searchParams.delete(appQueryParams.REFERRAL_PROVIDER_FILTER)
        searchParams.delete(appQueryParams.SORT_BY)
        searchParams.delete(appQueryParams.SORT_ORDER)
      }
      if (!pathString.includes(PAYMENTS_BASE_ROUTE)) {
        searchParams.delete(appQueryParams.PAYMENT_PROVIDER_FILTER)
        searchParams.delete(appQueryParams.STATUS)
      }
      if (!pathString.includes(appRoutes.referrals.DETAILS_ROUTE)) {
        searchParams.delete(appQueryParams.REFERRAL_ID)
      }
      if (!pathString.includes(AFFILIATE_RELATIONS_ACTIONS_ROUTE)) {
        searchParams.delete(appQueryParams.ACTION_TYPE)
      }
    },
    [],
  )

  useEffect(() => {
    addNewQueryParamIdsFromRedux(
      searchParams,
      practice.id,
      provider.id ?? "",
      selectedLocation?.id ?? "",
    )
    removeIdsIfNotNeeded(location.pathname, searchParams)

    const newSearch = searchParams.toString()
    const currentSearch = location.search.slice(1) // Remove the leading '?'

    if (newSearch !== currentSearch) {
      navigate(location.pathname, `?${newSearch}`, { replace: true })
    }
  }, [
    addNewQueryParamIdsFromRedux,
    location.pathname,
    location.search,
    navigate,
    practice.id,
    provider.id,
    removeIdsIfNotNeeded,
    searchParams,
    selectedLocation?.id,
  ])
}
